/**
 * @module app.shared
 */

import { Container, autoinject } from 'aurelia-framework';
import { IFormatOptions, IFormatter } from 'app/shared/lib/formatting/formatting-types';
import { TimestampFormatter, DateTimeFormatter, DateFormatter, TimeFormatter } from 'app/shared/lib/formatting/formatters-datetime';
import { TemplateFormatter, ListTextFormatter, CapitalizeFormatter, OptionalFormatter } from 'app/shared/lib/formatting/formatters-text';
import { NumberFormatter, IntegerFormatter, PercentFormatter, CurrencyFormatter, DecimalFormatter } from 'app/shared/lib/formatting/formatters-numeric';
import { AppLogger } from 'app/shared/lib/logging/app-logger';
import { stringSplitRetain } from 'app/shared/lib/utilities';

@autoinject
export class FormatterRegistry implements IFormatter {

    private registryMap = new Map<string, Function>();
    private logger = new AppLogger();

    constructor(private readonly container: Container) {
        this.logger = new AppLogger();
        this.logger.source = this;
        this.logger.target = 'console';
    }

    /**
     * Look up the registered formatter specified by `options.format` and
     * pass the value to that formatter (`IFormatter`).
     *
     * If `options.format` is not specified the value is returned unchanged.
     *
     * @param value the value to be formatted
     * @param options formatting options
     */
    public formatValue(value: any, options?: IFormatOptions): any {
        const format = options && options.format;
        if(!format) {
            return value;
        }

        const parts = this.parseFormatParts(format);
        const directOptions = { ...options, format: parts[1] };
        return this.formatDirect(value, parts[0], directOptions);
    }

    /**
     * Look up the registered formatter specified by `formatterName` and
     * pass the value to that formatter (`IFormatter`).
     *
     * Unlike `formatValue`, which exists to implement the `IFormatter`
     * interface and handle incoming requests from the `i18next` library,
     * this function skips a step of splitting `options.format` into
     * a formatter name and format specifier (using a `:` delimiter).
     *
     * @param value the value to be formatted
     * @param formatterName the name of the formatter to look up
     * @param options (*optional*) formatting options
     */
    public formatDirect(value: any, formatterName: string, options?: IFormatOptions): any {
        const constructor = this.registryMap.get(formatterName);
        if(!constructor) {
            this.logger.warn(`Cannot find formatter with name ${formatterName}.`);
            return value;
        }
        const formatter = this.container.get(constructor) as IFormatter;

        if(formatter && formatter.formatValue) {
            return formatter.formatValue(value, options);
        }

        return value;
    }

    /**
     * Register a formatter in the dependency injection container so that it may be used
     * to format values by the `i18next` library.
     *
     * The dependency injection contianer will be used to create an instance of the formatter
     * when it is used.
     *
     * @param name formatter name
     * @param formatterConstructor reference to the constructor of a class implementing `IFormatter`
     */
    public register<T extends IFormatter>(name: string, formatterConstructor: new (...args: any[]) => T): this {
        if(!name) { throw RangeError('Must specify a name for the formatter.'); }

        this.registryMap.set(name, formatterConstructor);
        this.container.registerSingleton(formatterConstructor);
        return this;
    }

    /**
     * Register the standard formatters
     */
    public registerStandardFormatters(): this {
        return this
            // date/time
            .register('timestamp', TimestampFormatter)
            .register('dateTime', DateTimeFormatter)
            .register('time', TimeFormatter)
            .register('date', DateFormatter)
            // number
            .register('number', NumberFormatter)
            .register('decimal', DecimalFormatter)
            .register('integer', IntegerFormatter)
            .register('percent', PercentFormatter)
            .register('currency', CurrencyFormatter)
            // text
            .register('optional', OptionalFormatter)
            .register('template', TemplateFormatter)
            .register('listText', ListTextFormatter)
            .register('capitalize', CapitalizeFormatter);
    }

    /**
     * Split `format` into two parts: the formatter name and the format specifier, delimited by `:`
     *
     * @param format the format string to parse
     */
    private parseFormatParts(format: string): [string, string] {
        const parts = stringSplitRetain(format, ':', 2);

        if(parts.length > 1) {
            return [parts[0], parts[1]];
        }
        return [parts[0], ''];
    }

}
